<template>

  <unauthed-panel :title="$t('alerts.sign_in_to_your_account')">

    <v-form @submit.prevent="submit" novalidate>

      <o-text-field
        :label="$t('titles.username_or_email_address')"
        prepend-inner-icon="$user"
        v-model.trim="v$.identifier.$model"
        :validator="v$.identifier"
        autofocus
        required
        autocomplete="username"
      />

      <o-text-field
        :label="$t('titles.password')"
        type="password"
        prepend-inner-icon="$password"
        v-model="v$.password.$model"
        :validator="v$.password"
        required
        autocomplete="current-password"
      />

			<div class="text-right mb-4">
				<router-link :to="{ name: 'forgotPassword' }">
					{{ $t('misc.forgot_your_password') }}
				</router-link>
			</div>

      <v-btn
        type="submit"
        block
        prepend-icon="$authenticate"
        :loading="submitting"
        :disabled="submitting"
      >
        {{ $t('actions.sign_in') }}
      </v-btn>

    </v-form>

	</unauthed-panel>

</template>

<script setup lang="ts">

import { inject, reactive, ref } from 'vue'
import { useTitle } from '@vueuse/core'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import UnauthedPanel from '../UnauthedPanel.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { required } from '../../utils/i18n-validators'

// misc
import store from '../../plugins/vuex'
import NotificationService from '../../services/NotificationService'
import type { Can } from '../../main'

const $can = inject('$can') as Can
const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const title = useTitle()
title.value = t('titles.ocra_log_in')

interface FormState {
  identifier: string
  password: string
}

const formState = reactive<FormState>({
  identifier: '',
  password: '',
});

const rules = {
  identifier: {
    required,
  },
  password: {
    required, 
  },
}

const v$ = useVuelidate(rules, formState)

/**
 * Submit
 */

const submitting = ref<boolean>(false)

async function submit() {

  // make sure the form is valid

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload = {
    username: formState.identifier,
    password: formState.password,
  }

  store.dispatch('auth/login', payload)
  	.then(() => {

      if ('to' in route.query && !Array.isArray(route.query.to)) {
        router.push(router.resolve(route.query.to!))
      } else if ($can('organizations.manage')) {
        router.push({name: 'organizationIndex'})
      } else {
        router.push({name: 'locationIndex'})
      }
    })
    .catch((e: any) => {
      console.error(e)
      const msg = t('alerts.invalid_login_credentials')
      NotificationService.error(msg)
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
